import { money } from '../utils/format';
import { canViewCosts } from '../utils/permissions';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Modal from '../components/UI/Modal';
import {
  statusFromAlerts, fmt, fmtDollar, relTime,
  filterAndSortLocations, aggregateTotals
} from '../utils/locationHelpers';
import {
  Plus, MapPin, Pencil, Search, Building2, Package, AlertTriangle,
  DollarSign, FileText, Eye, ShoppingCart, ArrowLeftRight,
  Clock, MapPinned
} from 'lucide-react';

export default function Locations() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [locations, setLocations] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', zip: '', phone: '' });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filter, setFilter] = useState('all');
  const [alertsOnly, setAlertsOnly] = useState(false);

  const load = async () => {
    const r = await api('/api/locations');
    setLocations(r.data);
    const sums = {};
    await Promise.all(r.data.map(async loc => {
      try { const sr = await api('/api/locations/' + loc.id + '/summary'); sums[loc.id] = sr.data; }
      catch (e) { sums[loc.id] = {}; }
    }));
    setSummaries(sums);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => aggregateTotals(locations, summaries), [locations, summaries]);
  const filtered = useMemo(
    () => filterAndSortLocations(locations, summaries, { search, filter, alertsOnly, sortBy }),
    [locations, summaries, search, sortBy, filter, alertsOnly]
  );

  const openAdd = () => { setForm({ name:'', address:'', city:'', state:'', zip:'', phone:'' }); setEditId(null); setModal('add'); };
  const openEdit = (loc, e) => { e.stopPropagation(); setForm({ name:loc.name||'', address:loc.address||'', city:loc.city||'', state:loc.state||'', zip:loc.zip||'', phone:loc.phone||'' }); setEditId(loc.id); setModal('edit'); };
  const submit = async () => {
    try {
      if (modal === 'edit') await api('/api/locations/' + editId, { method:'PUT', body:JSON.stringify(form) });
      else await api('/api/locations', { method:'POST', body:JSON.stringify(form) });
      setModal(null); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const canEdit = ['admin', 'manager'].includes(user?.role);
  const hasData = Object.keys(summaries).length > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <h1 style={{ fontFamily:'Lora,serif', fontSize:26 }}>Locations</h1>
        {user?.role === 'admin' && (
          <button className="btn btn-primary" onClick={openAdd}><Plus size={16} /> Add Location</button>
        )}
      </div>

      {/* Summary Strip */}
      {hasData && (
        <div style={{ display:'flex', gap:1, marginBottom:28, borderRadius:14, overflow:'hidden', border:'1px solid var(--border)' }}>
          {[
            { label:'Locations', val:totals.locations, icon:Building2, color:'#C9A84C' },
            { label:'Products',  val:totals.products,  icon:Package,   color:'#4A9EBF' },
            { label:'Alerts',    val:totals.alerts,     icon:AlertTriangle, color:'#E74C3C', urgent:totals.alerts>0 },
            { label:'Value',     val:canViewCosts(user) ? money(totals.value) : '•••', icon:DollarSign, color:'#2ECC71' },
            { label:'POs (30d)', val:totals.pos,        icon:FileText,  color:'#78A8E8' },
          ].map((k,i) => (
            <div key={i} style={{
              flex:1, padding:'18px 16px', textAlign:'center',
              background: k.urgent ? k.color+'08' : 'var(--card)',
              borderRight: i < 4 ? '1px solid var(--border)' : 'none', minWidth:0,
            }}>
              <k.icon size={16} style={{ color:k.color, opacity:.5, marginBottom:6 }} />
              <div style={{ fontFamily:'Lora,serif', fontSize:22, fontWeight:700, color:k.urgent?k.color:'var(--gold)', lineHeight:1 }}>{k.val}</div>
              <div style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginTop:5 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:'1 1 220px', minWidth:180 }}>
          <Search size={15} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--faint)' }} />
          <input placeholder="Search name, city, address..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft:36, width:'100%' }} />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width:'auto', minWidth:155 }}>
          <option value="name">Sort: Name A-Z</option>
          <option value="alerts">Sort: Alerts high-low</option>
          <option value="value">Sort: Value high-low</option>
          <option value="pos">Sort: POs high-low</option>
        </select>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ width:'auto', minWidth:140 }}>
          <option value="all">All Status</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="healthy">Healthy</option>
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--muted)', cursor:'pointer', whiteSpace:'nowrap', userSelect:'none' }}>
          <input type="checkbox" checked={alertsOnly} onChange={e => setAlertsOnly(e.target.checked)} style={{ width:'auto', accentColor:'#E74C3C' }} />
          Alerts only
        </label>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 20px' }}>
          <div style={{ width:64, height:64, borderRadius:16, background:'var(--card)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
            <MapPinned size={28} style={{ color:'var(--faint)', opacity:.5 }} />
          </div>
          <p style={{ fontSize:17, fontWeight:600, color:'var(--white)', marginBottom:6 }}>
            {search || filter !== 'all' || alertsOnly ? 'No locations match' : 'No locations yet'}
          </p>
          <p style={{ fontSize:13, color:'var(--faint)', maxWidth:320, margin:'0 auto' }}>
            {search || filter !== 'all' || alertsOnly ? 'Try a different search term or clear your filters.' : 'Add your first location to start tracking inventory.'}
          </p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(380px, 1fr))', gap:20 }}>
          {filtered.map(loc => <LocationCard key={loc.id} loc={loc} s={loc.s} canEdit={canEdit} nav={nav} onEdit={openEdit} />)}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <Modal title={modal === 'edit' ? 'Edit Location' : 'Add Location'} onClose={() => setModal(null)}>
          <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({...form, name:e.target.value})} required /></div>
          <div className="form-group"><label>Address</label><input value={form.address} onChange={e => setForm({...form, address:e.target.value})} /></div>
          <div className="grid-3">
            <div className="form-group"><label>City</label><input value={form.city} onChange={e => setForm({...form, city:e.target.value})} /></div>
            <div className="form-group"><label>State</label><input value={form.state} onChange={e => setForm({...form, state:e.target.value})} /></div>
            <div className="form-group"><label>ZIP</label><input value={form.zip} onChange={e => setForm({...form, zip:e.target.value})} /></div>
          </div>
          <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} /></div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>{modal === 'edit' ? 'Save Changes' : 'Add Location'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Location Card ── */
function LocationCard({ loc, s, canEdit, nav, onEdit }) {
  const status = statusFromAlerts(s.alert_products || 0);
  const alerts = s.alert_products || 0;
  const topAlerts = s.top_alerts || [];
  const value = s.inventory_value ? Number(s.inventory_value) : 0;

  // Card click navigates to location detail page
  const goToDetail = () => nav('/locations/' + loc.id);

  return (
    <div onClick={goToDetail}
      style={{
        background: status.glow !== 'transparent' ? status.glow : 'var(--card)',
        border:'1px solid var(--border)', borderRadius:16, overflow:'hidden',
        cursor:'pointer', transition:'all .18s ease', display:'flex', flexDirection:'column',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 32px rgba(0,0,0,.35)'; e.currentTarget.style.borderColor=status.color+'50'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor='var(--border)'; }}
    >
      {/* Header */}
      <div style={{ padding:'20px 22px 16px' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <h3 style={{ fontFamily:'Lora,serif', fontSize:20, fontWeight:700, color:'var(--white)', margin:0, lineHeight:1.2 }}>{loc.name}</h3>
              <span style={{ padding:'3px 10px', borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:'.6px', background:status.bg, color:status.color, flexShrink:0, lineHeight:'16px' }}>{status.label.toUpperCase()}</span>
            </div>
            <div style={{ fontSize:13, color:'var(--muted)', lineHeight:1.5 }}>
              <MapPin size={12} style={{ verticalAlign:'-1px', marginRight:5, opacity:.5 }} />
              {loc.address ? `${loc.address}, ${loc.city}, ${loc.state} ${loc.zip}` : `${loc.city||''} ${loc.state||''} ${loc.zip||''}`.trim() || 'No address'}
            </div>
            {loc.phone && <div style={{ fontSize:12, color:'var(--faint)', marginTop:2, paddingLeft:17 }}>{loc.phone}</div>}
          </div>
          {canEdit && (
            <button onClick={e => onEdit(loc, e)} title="Edit" style={{
              background:'transparent', border:'1px solid var(--border)', borderRadius:8,
              padding:'7px 9px', cursor:'pointer', color:'var(--faint)', transition:'all .12s', flexShrink:0,
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.color='var(--gold)'; e.currentTarget.style.background='var(--gold-pale)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--faint)'; e.currentTarget.style.background='transparent'; }}
            >
              <Pencil size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div style={{ padding:'0 22px 16px', display:'flex', gap:10 }}>
        <div style={{
          flex:'0 0 auto', minWidth:80, padding:'14px 16px', borderRadius:12, textAlign:'center',
          background: alerts > 0 ? status.bg : 'var(--navy)',
          border: alerts > 0 ? '1px solid '+status.color+'30' : '1px solid var(--border)',
        }}>
          <div style={{ fontFamily:'Lora,serif', fontSize:28, fontWeight:700, color:alerts>0?status.color:'#2ECC71', lineHeight:1 }}>{alerts}</div>
          <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.5px', color:alerts>0?status.color:'var(--muted)', marginTop:4, fontWeight:600 }}>Alerts</div>
        </div>
        <div style={{ flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          <StatChip label="Products" value={s.total_products ?? '-'} color="var(--gold)" />
          <StatChip label="POs (30d)" value={s.recent_pos ?? '-'} color="#78A8E8" />
          <div style={{ gridColumn:'1 / -1' }}>
            <StatChip label="Inventory Value" value={canViewCosts(user) ? (value ? money(value) : '-') : '•••'} color="#2ECC71" wide />
          </div>
        </div>
      </div>

      {/* Alert Context */}
      <div style={{ padding:'0 22px 14px', flex:1 }}>
        {alerts === 0 ? (
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:10, background:'rgba(46,204,113,.06)', border:'1px solid rgba(46,204,113,.12)' }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:'#2ECC71', flexShrink:0 }} />
            <span style={{ fontSize:13, color:'#2ECC71', fontWeight:500 }}>No immediate issues</span>
          </div>
        ) : (
          <div style={{ padding:'10px 14px', borderRadius:10, background:status.glow, border:'1px solid '+status.color+'18' }}>
            <div style={{ fontSize:12, fontWeight:600, color:status.color, marginBottom: topAlerts.length > 0 ? 8 : 0 }}>
              {alerts >= 16 ? 'Critical — review stock now' : alerts >= 6 ? 'Attention needed' : fmt(alerts) + ' items to review'}
            </div>
            {topAlerts.slice(0, 2).map((a, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, color:'var(--muted)', padding:'3px 0' }}>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:12 }}>{a.product_name}</span>
                <span style={{ fontWeight:700, color: Number(a.current_stock)===0?'#E74C3C':'#F39C12', flexShrink:0, fontSize:12 }}>
                  {fmt(a.current_stock)}<span style={{ color:'var(--faint)', fontWeight:400 }}>/{fmt(a.reorder_point)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding:'12px 22px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
        <div style={{ display:'flex', gap:14, fontSize:11, color:'var(--faint)' }}>
          <span title="Last count"><Clock size={11} style={{ verticalAlign:'-1px', marginRight:3 }} />{relTime(s.last_count)}</span>
          <span title="Last PO"><FileText size={11} style={{ verticalAlign:'-1px', marginRight:3 }} />{relTime(s.last_po)}</span>
        </div>
        <div style={{ display:'flex', gap:5 }}>
          <ActionBtn icon={Eye} color="#4A9EBF" label="Inventory" primary onClick={e => { e.stopPropagation(); nav('/inventory?location_id='+loc.id); }} />
          <ActionBtn icon={AlertTriangle} color="#E74C3C" label="Alerts" onClick={e => { e.stopPropagation(); nav('/inventory?location_id='+loc.id+'&status=reorder'); }} />
          <ActionBtn icon={ShoppingCart} color="#C9A84C" label="PO" onClick={e => { e.stopPropagation(); nav('/orders'); }} />
          <ActionBtn icon={ArrowLeftRight} color="#5BC4C0" label="Transfer" onClick={e => { e.stopPropagation(); nav('/transfers'); }} />
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, color, wide }) {
  return (
    <div style={{
      background:'var(--navy)', borderRadius:9, padding: wide ? '8px 14px' : '8px 12px',
      display:'flex', alignItems:'center', justifyContent: wide ? 'space-between' : 'center',
      flexDirection: wide ? 'row' : 'column', gap: wide ? 0 : 2, border:'1px solid var(--border)',
    }}>
      {wide && <span style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.4px' }}>{label}</span>}
      <span style={{ fontFamily:'Lora,serif', fontSize: wide ? 18 : 17, fontWeight:700, color, lineHeight:1 }}>{value}</span>
      {!wide && <span style={{ fontSize:9, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.4px' }}>{label}</span>}
    </div>
  );
}

function ActionBtn({ icon: Icon, color, label, primary, onClick }) {
  return (
    <button onClick={onClick} title={label} style={{
      display:'inline-flex', alignItems:'center', gap:4,
      padding: primary ? '5px 12px' : '5px 8px',
      background: primary ? color+'18' : 'transparent',
      border:'1px solid '+(primary ? color+'35' : 'var(--border)'),
      borderRadius:7, color: primary ? color : 'var(--faint)',
      cursor:'pointer', transition:'all .12s', fontSize:11, fontWeight:primary?600:500,
      fontFamily:'DM Sans,sans-serif', whiteSpace:'nowrap',
    }}
      onMouseEnter={e => { e.currentTarget.style.background=color+'22'; e.currentTarget.style.borderColor=color; e.currentTarget.style.color=color; e.currentTarget.style.transform='translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.background=primary?color+'18':'transparent'; e.currentTarget.style.borderColor=primary?color+'35':'var(--border)'; e.currentTarget.style.color=primary?color:'var(--faint)'; e.currentTarget.style.transform='none'; }}
    >
      <Icon size={12} />{primary ? label : null}
    </button>
  );
}
